import { uiText } from '../utils/i18n';
import { useState, useEffect, useCallback } from 'react';
import { useStore } from '../store';
import { useT } from '../utils/i18n';
import type { WSOutgoingMessage } from '../types';
import { ChatPage } from './ChatPage';
import { ContactsPage } from './ContactsPage';
import { SettingsPage } from './SettingsPage';
import { MemoryPage } from './MemoryPage';
import { SkillMarketPage } from './SkillMarketPage';
import { MeetingsPage } from './MeetingsPage';
import { BriefingsPage } from './BriefingsPage';
import { OntologyPage } from './OntologyPage';
import { UserProfileCard } from './UserProfileCard';

import { ErrorBoundary } from './ErrorBoundary';
import s from './Layout.module.css';

/* === Icons === */

function IconMemory({ active }: { active: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path
        d="M4 19.5A2.5 2.5 0 016.5 17H20"
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <path
        d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"
        fill={active ? '#a8d8ea' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
    </svg>
  );
}

function IconChat({ active }: { active: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path
        d="M4 5h16v11H9l-5 4V5z"
        fill={active ? '#b6c477' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      {active && (
        <>
          <circle cx={9} cy={11} r={1.1} fill={active ? '#1b1b14' : '#f1ecdf'} />
          <circle cx={13} cy={11} r={1.1} fill={active ? '#1b1b14' : '#f1ecdf'} />
          <circle cx={17} cy={11} r={1.1} fill={active ? '#1b1b14' : '#f1ecdf'} />
        </>
      )}
    </svg>
  );
}

function IconContacts({ active }: { active: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <circle
        cx={12}
        cy={9}
        r={3.2}
        fill={active ? '#f4a48a' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <path
        d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6"
        fill="none"
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
    </svg>
  );
}

function IconSkillMarket({ active }: { active: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      {/* Storefront icon */}
      <path
        d="M3 9l1.5-5h15L21 9"
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <path
        d="M3 9c0 1.5 1.2 2.5 2.25 2.5S7.5 10.5 7.5 9c0 1.5 1.2 2.5 2.25 2.5S12 10.5 12 9c0 1.5 1.2 2.5 2.25 2.5S16.5 10.5 16.5 9c0 1.5 1.2 2.5 2.25 2.5S21 10.5 21 9H3Z"
        fill={active ? '#d4a0e0' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <path
        d="M4 11.5V20h16v-8.5"
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <rect
        x={9} y={15} width={6} height={5}
        fill={active ? '#d4a0e0' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
    </svg>
  );
}

function IconMeetings({ active }: { active: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <rect
        x={3} y={5} width={14} height={11} rx={2}
        fill={active ? '#f4a48a' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <path
        d="M17 9l4-2v10l-4-2"
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
    </svg>
  );
}

function IconBriefings({ active }: { active: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      {/* Newspaper / briefing icon */}
      <rect
        x={3} y={3} width={18} height={18} rx={2}
        fill={active ? '#c79a3a' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <path d="M7 7h4v4H7z" fill={active ? '#1b1b14' : '#f1ecdf'} stroke="none" />
      <path d="M13 7h4" stroke={active ? '#1b1b14' : '#f1ecdf'} strokeWidth={2} />
      <path d="M13 11h4" stroke={active ? '#1b1b14' : '#f1ecdf'} strokeWidth={2} />
      <path d="M7 15h10" stroke={active ? '#1b1b14' : '#f1ecdf'} strokeWidth={2} />
      <path d="M7 19h7" stroke={active ? '#1b1b14' : '#f1ecdf'} strokeWidth={2} />
    </svg>
  );
}

function IconOntology({ active }: { active: boolean }) {
  const stroke = active ? '#1b1b14' : '#f1ecdf';
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6v5M7 18h10M7 18v-3M17 18v-3" stroke={stroke} strokeWidth={2} />
      <circle cx={12} cy={4} r={2.5} fill={active ? '#7ec9a8' : 'none'} stroke={stroke} strokeWidth={2} />
      <circle cx={7} cy={19} r={2.5} fill={active ? '#7ec9a8' : 'none'} stroke={stroke} strokeWidth={2} />
      <circle cx={17} cy={19} r={2.5} fill={active ? '#7ec9a8' : 'none'} stroke={stroke} strokeWidth={2} />
      <path d="M12 11L7 15M12 11l5 4" stroke={stroke} strokeWidth={2} />
    </svg>
  );
}

function IconFeedback() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#f1ecdf" strokeWidth={2} />
      <path d="M12 8v2m0 4h.01" stroke="#f1ecdf" strokeWidth={2} />
    </svg>
  );
}

function IconSettings({ active }: { active: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <circle
        cx={12}
        cy={12}
        r={3}
        fill={active ? '#c79a3a' : 'none'}
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
      <path
        d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
        fill="none"
        stroke={active ? '#1b1b14' : '#f1ecdf'}
        strokeWidth={2}
      />
    </svg>
  );
}

type Tab = 'memory' | 'chat' | 'contacts' | 'meetings' | 'skillMarket' | 'briefings' | 'ontology' | 'settings';

interface LayoutProps {
  send: (msg: WSOutgoingMessage) => void;
  sendBinary: (data: ArrayBuffer | Uint8Array) => void;
}

const FEEDBACK_CACHE_KEY = 'novax:feedback';

function FeedbackModal({ onClose }: { onClose: () => void }) {
  // Try cache first to avoid loading state
  const cached = localStorage.getItem(FEEDBACK_CACHE_KEY);
  const [content, setContent] = useState(cached || '');
  const [submitted, setSubmitted] = useState(!!cached);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return; // already have local data, skip fetch
    const token = localStorage.getItem('novax:token') || '';
    fetch('/auth/feedback', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d.feedback) {
          setContent(d.feedback.content);
          setSubmitted(true);
          localStorage.setItem(FEEDBACK_CACHE_KEY, d.feedback.content);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [cached]);

  const handleSubmit = useCallback(() => {
    if (!content.trim()) return;
    const token = localStorage.getItem('novax:token') || '';
    fetch('/auth/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content: content.trim() }),
    }).then(r => r.json()).then(() => {
      setSubmitted(true);
      localStorage.setItem(FEEDBACK_CACHE_KEY, content.trim());
    }).catch(() => {});
  }, [content]);

  return (
    <div className={s.modalOverlay} onClick={onClose}>
      <div className={s.modalBox} onClick={e => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <span>{uiText("反馈中心")}</span>
          <button className={s.modalClose} onClick={onClose}>✕</button>
        </div>
        {loading ? (
          <div className={s.modalBody}><p style={{ color: '#9E9E8E' }}>{uiText("加载中...")}</p></div>
        ) : (
          <div className={s.modalBody}>
            <textarea
              className={s.feedbackTextarea}
              value={content}
              onChange={e => { if (!submitted) setContent(e.target.value); }}
              placeholder={uiText("请输入您的反馈、建议或遇到的问题...")}
              readOnly={submitted}
            />
            {!submitted && (
              <button className={s.feedbackSubmitBtn} onClick={handleSubmit} disabled={!content.trim()}>
                {uiText("提交反馈")}
              </button>
            )}
            {submitted && <div className={s.feedbackBanner}>{uiText("✓ 反馈已提交，感谢您的意见！")}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export function Layout({ send, sendBinary }: LayoutProps) {
  const activeView = useStore((s) => s.activeView);
  const setView = useStore((s) => s.setView);
  const unreadDMs = useStore((s) => s.unreadDMs);
  const userName = useStore((s) => s.userName);
  const userAvatar = useStore((s) => s.userAvatar);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const t = useT();

  const currentTab: Tab =
    activeView === 'memory' ? 'memory'
    : activeView === 'team' ? 'contacts'
    : activeView === 'meetings' ? 'meetings'
    : activeView === 'skillMarket' ? 'skillMarket'
    : activeView === 'briefings' ? 'briefings'
    : activeView === 'ontology' ? 'ontology'
    : activeView === 'settings' ? 'settings'
    : 'chat';

  const handleNav = (tab: Tab) => {
    if (tab === 'memory') setView('memory');
    else if (tab === 'chat') setView('chat');
    else if (tab === 'contacts') setView('team');
    else if (tab === 'meetings') setView('meetings');
    else if (tab === 'skillMarket') setView('skillMarket');
    else if (tab === 'briefings') setView('briefings');
    else if (tab === 'ontology') setView('ontology');
    else if (tab === 'settings') setView('settings');
  };

  return (
    <div className={s.shell}>
      {/* Icon navigation bar */}
      <nav className={s.nav}>
        <button
          className={`${s.profileTrigger} ${showProfile ? s.profileTriggerActive : ''}`}
          onClick={() => setShowProfile((visible) => !visible)}
          aria-label={t('profile.open')}
          aria-expanded={showProfile}
        >
          {userAvatar
            ? <img src={userAvatar} alt="" />
            : <span>{(userName || localStorage.getItem('novax:displayName') || 'U').charAt(0).toUpperCase()}</span>}
        </button>
        <div className={s.navItems}>
          <button
            className={`${s.navBtn} ${currentTab === 'chat' ? s.navBtnActive : ''}`}
            onClick={() => handleNav('chat')}
            style={{ position: 'relative' }}
          >
            <IconChat active={currentTab === 'chat'} />
            <span>{t('nav.chat')}</span>
            {unreadDMs.size > 0 && currentTab !== 'chat' && (
              <span style={{
                position: 'absolute', top: 6, right: 10, width: 8, height: 8,
                borderRadius: '50%', background: '#e34a2a',
              }} />
            )}
          </button>
          <button
            className={`${s.navBtn} ${currentTab === 'memory' ? s.navBtnActive : ''}`}
            onClick={() => handleNav('memory')}
          >
            <IconMemory active={currentTab === 'memory'} />
            <span>{t('nav.memory')}</span>
          </button>
          <button
            className={`${s.navBtn} ${currentTab === 'contacts' ? s.navBtnActive : ''}`}
            onClick={() => handleNav('contacts')}
          >
            <IconContacts active={currentTab === 'contacts'} />
            <span>{t('nav.contacts')}</span>
          </button>
          <button
            className={`${s.navBtn} ${currentTab === 'meetings' ? s.navBtnActive : ''}`}
            onClick={() => handleNav('meetings')}
          >
            <IconMeetings active={currentTab === 'meetings'} />
            <span>{t('nav.meetings')}</span>
          </button>
          <button
            className={`${s.navBtn} ${currentTab === 'skillMarket' ? s.navBtnActive : ''}`}
            onClick={() => handleNav('skillMarket')}
          >
            <IconSkillMarket active={currentTab === 'skillMarket'} />
            <span>{t('nav.skillMarket')}</span>
          </button>
          <button
            className={`${s.navBtn} ${currentTab === 'briefings' ? s.navBtnActive : ''}`}
            onClick={() => handleNav('briefings')}
          >
            <IconBriefings active={currentTab === 'briefings'} />
            <span>{t('nav.briefings')}</span>
          </button>
          <button
            className={`${s.navBtn} ${currentTab === 'ontology' ? s.navBtnActive : ''}`}
            onClick={() => handleNav('ontology')}
          >
            <IconOntology active={currentTab === 'ontology'} />
            <span>{t('nav.ontology')}</span>
          </button>
        </div>
        <div className={s.navSpacer} />
        <button
          className={`${s.navBtn} ${currentTab === 'settings' ? s.navBtnActive : ''}`}
          onClick={() => handleNav('settings')}
        >
          <IconSettings active={currentTab === 'settings'} />
          <span>{t('nav.settings')}</span>
        </button>
        <button className={s.navBtn} onClick={() => setShowFeedback(true)} style={{ marginBottom: 8 }}>
          <IconFeedback />
          <span>{uiText("反馈")}</span>
        </button>
      </nav>

      {/* Main content area — keep all tabs mounted, toggle visibility */}
      <main className={s.main}>
        <ErrorBoundary>
          <div style={{ display: currentTab === 'chat' ? 'contents' : 'none' }}>
            <ChatPage send={send} sendBinary={sendBinary} />
          </div>
          <div style={{ display: currentTab === 'contacts' ? 'contents' : 'none' }}>
            <ContactsPage send={send} />
          </div>
          <div style={{ display: currentTab === 'memory' ? 'contents' : 'none' }}>
            <MemoryPage />
          </div>
          <div style={{ display: currentTab === 'meetings' ? 'contents' : 'none' }}>
            <MeetingsPage />
          </div>
          <div style={{ display: currentTab === 'skillMarket' ? 'contents' : 'none' }}>
            <SkillMarketPage />
          </div>

          <div style={{ display: currentTab === 'briefings' ? 'contents' : 'none' }}>
            <BriefingsPage />
          </div>
          <div style={{ display: currentTab === 'ontology' ? 'contents' : 'none' }}>
            <OntologyPage />
          </div>
          <div style={{ display: currentTab === 'settings' ? 'contents' : 'none' }}>
            <SettingsPage />
          </div>
        </ErrorBoundary>
      </main>
      {showProfile && <UserProfileCard onClose={() => setShowProfile(false)} />}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </div>
  );
}
